/**
 * Dependencies.
 */
import moment from 'moment-timezone';
import { Component, Mixins } from 'vue-property-decorator';

import { addressCoordinatesService } from '@/api/address/coordinates.service';
import { addressReverseService } from '@/api/address/reverse.service';
import applicationConstant from '@/modules/main/constants/application.constant';
import { removeEmojis } from '@/modules/main/utils/helpers';
import { calculateDistanceBetweenTwoPoints } from '@/modules/main/utils/map';
import MapChangeMarker from '@/modules/tracking/components/map/change-marker.component.vue';
import {
  ORDER_REQUEST_STATUS,
  ORDER_REQUEST_TYPES,
  TRACKING_ERRORS
} from '@/modules/tracking/constants';
import TimelineMixin from '@/modules/tracking/mixins/timeline.mixin';
import TrackingMixin from '@/modules/tracking/mixins/tracking.mixin';
import { trackingStore } from '@/modules/tracking/store';
import configuration from '@/plugins/configuration.plugin';
import Error from '@/ui/components/error.component.vue';
import NotifyClientRequest from '@/ui/components/notify/client-request.component.vue';
import Timeline from '@/ui/components/timeline/timeline.component.vue';

/**
 * Interface custom coordinates.
 */
interface ICustomCoordinates {
  distanceFromOriginal?: number;

  isForAutomaticApproval?: boolean;

  latitude: number;

  longitude: number;
}

/**
 * Component.
 */
@Component({
  components: {
    Error,
    MapChangeMarker,
    NotifyClientRequest,
    Timeline
  },
  name: 'change-address-scene'
})
export default class extends Mixins(TrackingMixin, TimelineMixin) {
  /**
   * Property `additionalInformation`.
   */
  additionalInformation: string = null;

  /**
   * Property `doorNumber`.
   */
  doorNumber: string = null;

  /**
   * Property `canEditMarker`.
   */
  canEditMarker = false;

  /**
   * Property `customCoordinates`.
   */
  customCoordinates: ICustomCoordinates = null;

  /**
   * Property `lastCustomCoordinatesBeforeCancel`.
   */
  lastCustomCoordinatesBeforeCancel: ICustomCoordinates = null;

  /**
   * Property `hasValidCoordinates`.
   */
  hasValidCoordinates = false;

  /**
   * Property `isLoadingCoordinates`.
   */
  isLoadingCoordinates = true;

  /**
   * Property `distributionCenterRadius`.
   */
  distributionCenterRadius = null;

  /**
   * Property `isConfirmingAddress`.
   */
  isConfirmingAddress = false;

  /**
   * Property `isSnackbarVisible`.
   */
  isSnackbarVisible = false;

  /**
   * Property `isInvalidZipCode`.
   */
  isInvalidZipCode = false;

  /**
   * Property `reverseAddress`.
   */
  reverseAddress = null;

  /**
   * Getter `newDate`.
   */
  get newDate() {
    const date = trackingStore.dayChanged;

    return moment(date).isValid() ? moment(date).format('DD MMM YYYY') : null;
  }

  /**
   * Getter `newAddress`.
   */
  get newAddress() {
    const address = this.reverseAddress
      ? this.reverseAddress
      : trackingStore?.addressChanged;

    const {
      city = null,
      countryCode = null,
      countryName = null,
      doorNumber = null,
      isValidated = false,
      latitude = null,
      longitude = null,
      street = null,
      zipCode = null
    } = address || {};

    return {
      city,
      countryCode,
      countryName,
      doorNumber,
      isValidated,
      latitude,
      longitude,
      street,
      zipCode
    };
  }

  /**
   * Getter `hasCustomCoordinates`.
   */
  get hasCustomCoordinates() {
    const { latitude, longitude } = this.customCoordinates || {};

    return !!latitude && !!longitude;
  }

  /**
   * Getter `newMarkerCoordinates`.
   */
  get newMarkerCoordinates() {
    const location = this.hasCustomCoordinates
      ? this.customCoordinates
      : trackingStore?.addressChanged;
    const {
      isForAutomaticApproval = false,
      latitude = null,
      longitude = null
    } = location || {};

    return { isForAutomaticApproval, latitude, longitude };
  }

  /**
   * Getter `originalMarkerCoordinates`.
   */
  get originalMarkerCoordinates() {
    const { order = null } = trackingStore || {};
    const { address = null } = order || {};
    const { latitude = null, longitude = null } = address || {};

    return { latitude, longitude };
  }

  /**
   * Getter `customLatitude`.
   */
  get customLatitude() {
    const { latitude = null } = this.customCoordinates || {};

    return latitude ? `${String(latitude).substring(0, 9)}, ` : '-, ';
  }

  /**
   * Getter `customLongitude`.
   */
  get customLongitude() {
    const { longitude = null } = this.customCoordinates || {};

    return longitude ? `${String(longitude).substring(0, 9)}` : '- ';
  }

  /**
   * Getter is status processing.
   */
  get isStatusProcessing() {
    const { status: { code = null } = {} } = this.currentTracking || {};

    return code === this.trackingStatus.PROCESSING;
  }

  /**
   * Getter is status in routing.
   */
  get isStatusInRouting() {
    const { status: { code = null } = {} } = this.currentTracking || {};

    return code === this.trackingStatus.IN_ROUTING;
  }

  /**
   * Getter has today address request.
   */
  get hasTodayAddressRequest() {
    const { requests = [] } = trackingStore || {};
    const today = moment().startOf('day');

    const todayDateRequest = requests.filter(
      ({ recordDate = null, status, type }) =>
        moment(recordDate).isValid() &&
        moment(recordDate)
          .startOf('day')
          .isSame(today) &&
        type === ORDER_REQUEST_TYPES.CHANGE_ADDRESS &&
        (status.code === ORDER_REQUEST_STATUS.AUTOMATIC_PENDING_TREATMENT ||
          status.code === ORDER_REQUEST_STATUS.TREATED)
    );

    return todayDateRequest.length > 0;
  }

  /**
   * Getter `applyRadius`.
   */
  get applyRadius() {
    const { startDate = null } = trackingStore.route || {};
    const {
      isStatusInRouting,
      isStatusProcessing,
      newDate: hasNewDateChangeRequest
    } = this || {};
    let isRouteForToday = false;

    const today = moment().startOf('day');

    if (startDate && moment(startDate).isValid()) {
      const startDateTruncated = moment(startDate).startOf('day');

      isRouteForToday = startDateTruncated.isSame(today);
    }

    return (
      !hasNewDateChangeRequest &&
      isRouteForToday &&
      (isStatusProcessing || isStatusInRouting)
    );
  }

  /**
   * Lifecycle mounted hook.
   */
  async mounted() {
    const { addressChanged: address = null } = trackingStore || {};

    if (!address) {
      this.handleForceTrackingScene();
    } else {
      this.$on('trackingDataUpdated', async () => {
        const isValid = this.handleIsValidToBeOnScene();

        if (!isValid) {
          this.handleForceTrackingScene();
        }
      });

      await this.handleInitializeMixin();

      const isValid = this.handleIsValidToBeOnScene();

      if (!isValid) {
        this.handleForceTrackingScene();
      } else {
        await this.handleGetConfiguration();

        this.handleGetCoordinates();

        const { isForAutomaticApproval, latitude = null, longitude = null } =
          this.newMarkerCoordinates || {};
        this.customCoordinates = {
          isForAutomaticApproval,
          latitude,
          longitude
        };
      }
    }
  }

  /**
   * Handle is valid to be on scene.
   */
  handleIsValidToBeOnScene() {
    return this.canCreateChangeRequest;
  }

  /**
   * Handle get configuration.
   */
  async handleGetConfiguration() {
    const config = await configuration.getDistributionCenterRadius();
    const { distributionCenterRadius = null } = config || {};

    this.distributionCenterRadius = distributionCenterRadius;
  }

  /**
   * Handle start edit marker.
   */
  handleStartEditMarker() {
    const { isForAutomaticApproval, latitude = null, longitude = null } =
      this.newMarkerCoordinates || {};

    this.canEditMarker = true;
    this.customCoordinates = { isForAutomaticApproval, latitude, longitude };
    this.lastCustomCoordinatesBeforeCancel = Object.assign(
      {},
      this.customCoordinates
    );
  }

  /**
   * Handle marker update
   */
  handleMarkerUpdate({
    distance = 0,
    isForAutomaticApproval,
    latitude = null,
    longitude = null
  }: {
    distance: number;
    isForAutomaticApproval: boolean;
    latitude: number;
    longitude: number;
  }) {
    if (!!latitude && !!longitude) {
      this.customCoordinates = {
        distanceFromOriginal: distance,
        isForAutomaticApproval,
        latitude,
        longitude
      };
    }
  }

  /**
   * Handle cancel pin edition.
   */
  handleCancelPinEdition() {
    this.customCoordinates = Object.assign(
      {},
      this.lastCustomCoordinatesBeforeCancel
    );
    this.canEditMarker = false;
  }

  /**
   * Handle get coordinates.
   */
  async handleGetCoordinates() {
    const { addressChanged: address = null } = trackingStore || {};
    const { city, doorNumber, isValidated = false, street, zipCode } =
      address || {};

    if (address && !isValidated) {
      let query = `?street=${encodeURIComponent(street)}`;
      query = `${query}&doorNumber=${encodeURIComponent(doorNumber)}`;
      query = `${query}${
        zipCode ? `&zipCode=${encodeURIComponent(zipCode)}` : ''
      }`;
      query = `${query}&city=${encodeURIComponent(city)}`;
      query = `${query}&trackingNumber=${encodeURIComponent(
        this.trackingNumber
      )}`;
      query = `${query}&orderType=${encodeURIComponent(
        this.isDelivery ? 'DELIVERY' : 'COLLECT'
      )}`;

      try {
        this.isLoadingCoordinates = true;

        const response = await addressCoordinatesService.GET(
          `${addressCoordinatesService.getModelEndpoint}${query}`
        );

        const { latitude = null, longitude = null } = response || {};

        trackingStore.setChangeDeliveryAddress({
          address: {
            ...address,
            isValidated: !!latitude && !!longitude,
            latitude,
            longitude
          }
        });

        this.hasValidCoordinates = !!latitude && !!longitude;
      } catch (error) {
        if (process.env.BUILD_MODE !== 'prd') {
          console.error(error);
        }

        this.hasValidCoordinates = false;

        trackingStore.setChangeDeliveryAddress({
          address: {
            ...address,
            isValidated: false,
            latitude: null,
            longitude: null
          }
        });
      }
    }

    if (isValidated) {
      this.hasValidCoordinates = true;
    }

    this.isLoadingCoordinates = false;
  }

  /**
   * Handle confirm adjust pin.
   */
  async handleConfirmAdjustPin() {
    let reverseAddress = null;

    try {
      if (this.canEditMarker) {
        this.isConfirmingAddress = true;

        const { latitude = null, longitude = null } =
          this.customCoordinates || {};

        let query = '';

        query = `${query}${
          latitude ? `?latitude=${encodeURIComponent(latitude)}` : ''
        }`;
        query = `${query}${
          longitude ? `&longitude=${encodeURIComponent(longitude)}` : ''
        }`;
        query = `${query}${
          this.trackingNumber
            ? `&trackingNumber=${encodeURIComponent(this.trackingNumber)}`
            : ''
        }`;
        query = `${query}&orderType=${encodeURIComponent(
          this.isDelivery ? 'DELIVERY' : 'COLLECT'
        )}`;

        this.isSnackbarVisible = false;
        this.isInvalidZipCode = false;

        reverseAddress = await addressReverseService.GET(
          `${addressReverseService.getModelEndpoint}${query}`
        );
      }
    } catch (error) {
      const { raw = null } = error || {};
      const { code = null } = raw || {};

      switch (code) {
        case TRACKING_ERRORS.ZIP_CODE_OUT_OF_RANGE:
          this.$router.push({
            name: 'OutOfRange',
            params: {
              key: this.key
            }
          });
          break;

        default:
          this.isSnackbarVisible = true;
          this.isInvalidZipCode = true;
          break;
      }
    } finally {
      this.isConfirmingAddress = false;
      this.canEditMarker = false;
    }

    const { latitude: lat1, longitude: long1 } =
      trackingStore.addressChanged || {};
    const { latitude: lat2, longitude: long2 } = this.customCoordinates || {};
    const distance = calculateDistanceBetweenTwoPoints({
      marker1: [lat1, long1],
      marker2: [lat2, long2]
    });
    const { minDistanceAdjustPin = '' } = applicationConstant || {};

    this.reverseAddress =
      distance > minDistanceAdjustPin
        ? reverseAddress
        : trackingStore.addressChanged;
  }

  /**
   * Handle confirm.
   */
  async handleConfirm() {
    try {
      const { dayChanged: date = null } = trackingStore || {};

      const {
        isForAutomaticApproval = false,
        latitude = null,
        longitude = null
      } = this.customCoordinates || {};

      this.doorNumber = removeEmojis(this.doorNumber);
      this.additionalInformation = removeEmojis(this.additionalInformation);

      trackingStore.setChangeDeliveryAddress({
        address: {
          ...this.newAddress,
          additionalInformation: this.additionalInformation,
          doorNumber: this.doorNumber
            ? this.doorNumber
            : this.newAddress.doorNumber,
          isForAutomaticApproval:
            this.distributionCenterRadius > 0 && !this.hasTodayAddressRequest
              ? this.applyRadius
                ? isForAutomaticApproval
                : true
              : false,
          latitude,
          longitude
        }
      });

      this.$router.push({
        name: 'ConfirmChange',
        params: {
          key: this.key,
          type: date ? 'day_address' : 'address'
        }
      });
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error(error);
      }
    }
  }

  /**
   * Handle cancel.
   */
  handleCancel() {
    this.$router.push({
      name: 'Tracking',
      params: {
        key: this.key
      }
    });
  }
}
